// When TripDTO will be moved to shared change the path to:
// eslint-disable-next-line import/no-restricted-paths
import { MessageDTO } from '../types';
import { TripDTO } from '../modules/trip/types/trip';

export interface TripMapMatcher {
  normalizeTrip(trip: TripDTO): Promise<TripDTO>;
  normalizeTrips(trips: TripDTO[]): Promise<TripDTO[]>;
  normalizeMessages(messages: MessageDTO[]): Promise<MessageDTO[]>;
}

export interface MapboxConfig {
  accessToken: string;
  maxPointsPerBatch?: number;
  minPointsThreshold?: number;
  radiusMeters?: number;
}

export const DEFAULT_MAPBOX_CONFIG: Required<MapboxConfig> = {
  accessToken: '',
  maxPointsPerBatch: 100,
  minPointsThreshold: 20,
  radiusMeters: 30,
};

export class MapboxTripMapMatcher implements TripMapMatcher {
  private config: Required<MapboxConfig>;

  constructor(config: MapboxConfig) {
    this.config = { ...DEFAULT_MAPBOX_CONFIG, ...config };
    if (!this.config.accessToken) {
      throw new Error('Mapbox access token is required');
    }
  }

  public async normalizeTrip(trip: TripDTO): Promise<TripDTO> {
    const originalMessages = trip.messages;
    const normalizedMessages = await this.normalizeMessages(
      originalMessages as MessageDTO[],
    );

    return {
      ...trip,
      messages: normalizedMessages,
    };
  }

  public async normalizeTrips(trips: TripDTO[]): Promise<TripDTO[]> {
    const tasks = trips.map((trip) => this.normalizeTrip(trip));
    return Promise.all(tasks);
  }

  public async normalizeMessages(
    messages: MessageDTO[],
  ): Promise<MessageDTO[]> {
    // 1. Sanitize / thin data
    const sanitized = this.sanitizeMessages(messages);
    if (sanitized.length < this.config.minPointsThreshold) {
      // not enough points to justify map-matching => return original
      return messages;
    }

    // 2. Batch them
    const batches = this.createBatches(sanitized);

    // 3. Send each batch to Mapbox with concurrency limit
    const matchedBatchPromises = batches.map((batch) =>
      this.mapboxMatchBatch(batch),
    );
    const matchedBatches = await Promise.all(matchedBatchPromises);

    // 4. Flatten matched points
    const matchedPoints = matchedBatches.flat();

    // 5. Map matched points back to the original messages
    return this.mapMatchedBack(messages, matchedPoints);
  }

  private sanitizeMessages(
    messages: MessageDTO[],
  ): (MessageDTO & { originalIndex: number })[] {
    const sanitized: Array<MessageDTO & { originalIndex: number }> = [];
    const seen = new Set<string>();

    messages.forEach((msg, i) => {
      const key = `${msg.latitude},${msg.longitude}`;
      if (!seen.has(key)) {
        sanitized.push({ ...msg, originalIndex: i });
        seen.add(key);
      }
    });

    return sanitized;
  }

  private createBatches(
    messages: Array<MessageDTO & { originalIndex: number }>,
  ): Array<Array<MessageDTO & { originalIndex: number }>> {
    const batches: Array<Array<MessageDTO & { originalIndex: number }>> = [];
    const { maxPointsPerBatch } = this.config;
    for (let i = 0; i < messages.length; i += maxPointsPerBatch) {
      batches.push(messages.slice(i, i + maxPointsPerBatch));
    }
    return batches;
  }

  private async mapboxMatchBatch(
    batch: Array<MessageDTO & { originalIndex: number }>,
  ): Promise<Array<MessageDTO & { originalIndex: number }>> {
    // Build coords query param
    const coords = batch
      .map((msg) => `${msg.longitude},${msg.latitude}`)
      .join(';');
    // Radii query param: one radius per coordinate
    const radii = batch
      .map(() => this.config.radiusMeters.toString())
      .join(';');

    // Construct URL
    const url = `https://api.mapbox.com/matching/v5/mapbox/driving/${coords}?geometries=geojson&radiuses=${radii}&access_token=${this.config.accessToken}`;
    let data: any;
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`Mapbox returned status ${resp.status}`);
      }
      data = await resp.json();
    } catch (err) {
      console.error('Error calling Mapbox:', err);
      // fallback: return the batch unmodified
      return batch;
    }

    // parse tracepoints
    const tracepoints = data.tracepoints || [];
    return batch.map((msg, idx) => {
      const tp = tracepoints[idx];
      if (!tp || !tp.location) {
        return msg; // fallback
      }
      return {
        ...msg,
        latitude: tp.location[1],
        longitude: tp.location[0],
      };
    });
  }

  private mapMatchedBack(
    original: MessageDTO[],
    matched: Array<MessageDTO & { originalIndex: number }>,
  ): MessageDTO[] {
    // We'll create a copy to avoid mutating the original
    const updated = [...original];

    // For each matched item, update the corresponding original message
    matched.forEach((mp) => {
      const i = mp.originalIndex;
      if (i >= 0 && i < updated.length) {
        updated[i] = {
          ...updated[i],
          latitude: mp.latitude,
          longitude: mp.longitude,
        };
      }
    });

    return updated;
  }
}
